import { Anchor, Badge, Button, Card, Flex, Grid, Switch, Text, Tooltip } from '@mantine/core';
import { IconCalendarOff, IconChartLine, IconHistory, IconLayoutBoard, IconSettings, IconUsers } from '@tabler/icons-react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import HeaderStatistic from '../../components/HeaderStatistic';
import LazyLoadSpinner from '../../components/LazyLoaderSpinner';
import ListingPage from '../../components/ListingPage';
import prettifiedLabel from '../../constants/labelPrettifier';
import useFetch from '../../helpers/useFetch';

const StoryCard = ({ story, stats }) => {
  const fetch = useFetch();
  const location = useLocation();
  const { accountId, domainId } = useParams();
  const currentDomain = useSelector((state) => state.domain.domain);
  const domainCurrency = currentDomain.currency;
  const [running, setRunning] = useState(story.running);

  const linkToSegment = story.segment
    ? `#/accounts/${accountId}/domains/${domainId}/segments/${story.segment.id}`
    : null;

  const linkToProfileFilterSegment = story.realtimeSegment
    ? `#/accounts/${accountId}/domains/${domainId}/segments/${story.realtimeSegment.id}`
    : null;

  const isExpired = story.endTime && new Date(story.endTime).getTime() < new Date().getTime();

  let statsElements;

  if (!stats) {
    statsElements = (
      <Grid.Col span={12}>
        <Grid justify="center">
          <LazyLoadSpinner />
        </Grid>
      </Grid.Col>
    );
  } else {
    statsElements = (
      <>
        <Grid.Col span={2} ta="center">
          <HeaderStatistic title="Impressions" stats={stats} stat="impressions" id={story.token} statKey="count" />
        </Grid.Col>
        <Grid.Col span={2} ta="center">
          <HeaderStatistic
            title="Slide Views"
            stats={stats}
            stat="slideViews"
            id={story.token}
            percentageOfStat="impressions"
            statKey="count"
          />
        </Grid.Col>
        <Grid.Col span={2} ta="center">
          <HeaderStatistic
            title="CTA Clicks"
            stats={stats}
            stat="clicks"
            id={story.token}
            percentageOfStat="impressions"
            statKey="count"
          />
        </Grid.Col>
        <Grid.Col span={2} ta="center">
          <HeaderStatistic
            title="Conversions"
            stats={stats}
            stat="conversions"
            percentageOfStat="clicks"
            id={story.token}
            statKey="count"
          />
        </Grid.Col>
        <Grid.Col span={2} ta="center">
          <HeaderStatistic
            title="Purchases"
            stats={stats}
            stat="purchases"
            percentageOfStat="conversions"
            id={story.token}
            statKey="count"
          />
        </Grid.Col>
        <Grid.Col span={2} ta="center">
          <HeaderStatistic
            title="Revenue"
            stats={stats}
            stat="purchases"
            id={story.token}
            statKey="value"
            currency={domainCurrency}
          />
        </Grid.Col>
      </>
    );
  }

  return (
    <Card mb="md" withBorder>
      <Card.Section inheritPadding py="sm">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Flex align="center" gap="sm" mb="xs">
              <Anchor href={`#${location.pathname}/${story.id}/details`} fw={600}>
                {story.name}
              </Anchor>
              {isExpired && (
                <Text c="dimmed" size="sm">
                  <IconCalendarOff size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Expired
                </Text>
              )}
            </Flex>
            <Flex gap="xs" wrap="wrap">
              <Badge variant="default">
                <IconUsers size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                <Anchor href={linkToSegment} target="_blank" size="xs">
                  {story.segment?.name || 'Unknown'} {story.inverseSegmentSelection ? '(exclude)' : ''}
                </Anchor>
              </Badge>
              {story.realtimeSegment && (
                <Badge variant="default">
                  <IconUsers size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  <Anchor href={linkToProfileFilterSegment} target="_blank" size="xs">
                    {story.realtimeSegment?.name || 'Unknown'}
                  </Anchor>
                </Badge>
              )}
              {story.page && (
                <Badge variant="default">
                  <IconLayoutBoard size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  {story.page.name}
                </Badge>
              )}
              <Badge variant="default">
                <IconSettings size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {prettifiedLabel(story.type)}
              </Badge>
              <Tooltip
                label={
                  <>
                    <div>Created: {new Date(story.createdAt).toLocaleString()}</div>
                    <div>Updated: {new Date(story.updatedAt).toLocaleString()}</div>
                  </>
                }
                position="top"
                withArrow
              >
                <Badge variant="default" style={{ cursor: 'pointer' }}>
                  <IconHistory size={14} style={{ verticalAlign: 'middle' }} />
                </Badge>
              </Tooltip>
            </Flex>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Flex justify="flex-end" align="flex-start" gap="sm">
              <Button component="a" href={`#${location.pathname}/${story.id}/analytics`} size="xs">
                Statistics <IconChartLine size={14} style={{ marginLeft: 4 }} />
              </Button>
              <Switch
                size="lg"
                checked={running}
                onChange={(e) => {
                  const newRunning = e.target.checked;
                  setRunning(newRunning);
                  fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/stories/${story.id}`, {
                    method: 'PUT',
                    body: JSON.stringify({ running: newRunning }),
                  }).catch(() => setRunning(!newRunning));
                }}
              />
            </Flex>
          </Grid.Col>
        </Grid>
      </Card.Section>
      <Card.Section inheritPadding py="sm">
        <Grid>{statsElements}</Grid>
      </Card.Section>
    </Card>
  );
};

const StoryCards = ({ storys, stats }) => {
  return storys.map((s, i) => <StoryCard key={i} story={s} stats={stats} />);
};

const StoriesPage = () => {
  return (
    <ListingPage
      entityType="story"
      useStats={true}
      filterBy="name"
      Cards={StoryCards}
      createRestrictionKey="storiesCreate"
    />
  );
};

export default StoriesPage;
